import { Exclude, Expose, Transform, Type } from 'class-transformer';
import { Feature, Point } from 'geojson';

import { Default, Plunk, ExposeRawData, PlunkArray } from '@lib/common/decorators';
import { SnCollection, SnModel } from '@lib/common/models';
import { ISnIsSharable } from '@lib/share';
import { COLORS } from '@lib/ui/theme';

import { SnContentType, SnPullProtocol, IHasContentType } from '../base';
import { SnCameraGroup } from '../camera-group';
import { FususRegionCode } from '../constants';
import { FsAxonDevice } from '../layer/integrations/axon-devices';
import { FsPresenceDevice } from '../layer/integrations/presence';
import { SnFloor, SnLocation } from '../location';
import { SnOrganization } from '../organization';
import { SnShareInfo, SnSharePermissions } from '../share';
import { SnDVRStream, SnStreamSources, SnLiveVideoUrls } from '../stream';
import { SnDVRSources } from '../stream/dvr-source';
import { SnTag } from '../tag';
import { SnCameraAiDetectionType } from './camera-ai-detection-type';
import { SnCameraControlType } from './camera-control-type';
import { SnDiscoveryUrls } from './camera-discovery-urls';
import { SnCameraDisposition } from './camera-disposition';
import { CameraExternal } from './camera-external';
import { SnCameraManagementType } from './camera-management-type';
import { SnNimbleUrls } from './camera-nimble-urls-type';
import { SnCameraProtocol } from './camera-protocol';
import { SnCameraStatus } from './camera-status';
import { SnCameraType } from './camera-type';
import { SnCameraUnmanagedType } from './camera-unmanaged-type';

export class SnCamera extends SnModel implements ISnIsSharable, IHasContentType {
  @Expose()
  public id: string;

  @Expose()
  public name: string;

  @Expose()
  public make: string;

  @Expose()
  public model: string;

  @Expose()
  public starred: boolean;

  @Expose()
  public transcode: boolean;

  @Expose()
  public deepIntegrationMake: string;

  @Expose()
  @ExposeRawData()
  @PlunkArray('id', { toPlainOnly: true, groups: ['form'] })
  public groups: SnCameraGroup[];

  @Expose()
  @Default(false)
  public isSubEnabled: boolean;

  @Expose()
  public transcodeSourceProtocol: SnCameraProtocol;

  @Expose()
  public transcodeCommand: string;

  @Expose()
  @Exclude({ toPlainOnly: true })
  public status: SnCameraStatus;

  @Expose()
  @Default(SnCameraControlType.Fixed)
  public type: SnCameraControlType;

  @Expose()
  @Plunk('id', { toPlainOnly: true, groups: ['form'] })
  @Type(() => SnCameraType)
  public cameraType: SnCameraType;

  @Expose()
  @Default(SnCameraDisposition.External)
  public disposition: SnCameraDisposition;

  @Expose()
  @Type(() => SnLocation)
  @Plunk('id', { toPlainOnly: true, groups: ['form'] })
  public location: SnLocation;

  @Expose()
  @Type(() => SnFloor)
  @Plunk('id', { toPlainOnly: true, groups: ['form'] })
  public floor: SnFloor;

  @Expose()
  @ExposeRawData()
  public geometry: Point;

  @Expose()
  @Default(90)
  public heading: number;

  @Expose({ name: 'nimbleUrls' })
  public nimbleUrls?: SnNimbleUrls;

  @Expose()
  public altitude: number;

  @Expose()
  public fieldOfView: number;

  @Expose()
  public isLive: boolean;

  @Expose()
  public isCitizen?: boolean;

  @Expose()
  public region?: FususRegionCode;

  @Expose()
  @Type(() => SnStreamSources)
  public liveUrls: SnStreamSources;

  @Expose()
  @Type(() => SnLiveVideoUrls)
  public liveVideoUrls: SnLiveVideoUrls;

  @Expose()
  public discoveryUrls: SnDiscoveryUrls;

  @Expose()
  public distance: number;

  @Expose()
  public onvifPort: number;

  @Expose()
  public canExportVideo: boolean;

  @Expose()
  public appliance: string;

  @Expose()
  public aiAppliance: string;

  @Expose()
  public liveApp: string;

  @Expose()
  public readonly streamFilename: string;

  @ExposeRawData()
  public cameraMeta: any;

  @Expose()
  public onvifHost: string;

  @Expose()
  public host: string;

  @Expose()
  public port: number;

  @Expose()
  public username: string;

  @Expose()
  public password: string;

  @Expose()
  public path: string;

  @Expose()
  public pathSub: string;

  @Expose()
  public aiPathExt: string;

  @Expose()
  public aiStreamType: number;

  @Expose()
  public idleTime: number;

  @Expose()
  public isRestricted: boolean;

  @Expose()
  public isRestrictionActive: boolean;

  @Expose()
  public isFususCamera: boolean;

  @Expose()
  public isEagleEyeNetworks: boolean;

  @Expose()
  public aiPullCamera: boolean;

  @Expose()
  public aiFrameTimeout: number;

  @Expose()
  public isAiCamera: boolean;

  @Expose()
  public isAiEnabled: boolean;

  @Expose()
  public authDigestType: string;

  @Expose()
  public supportedAiDetections: any[];

  @Expose()
  @Default(SnPullProtocol.RTSP)
  public protocol: SnPullProtocol;

  @Expose()
  @Default(false)
  public ssl: boolean;

  @Expose()
  @Default(false)
  public isPublic: boolean;

  @Expose()
  @Default(false)
  public record: boolean;

  @Expose()
  @Default(30)
  public loopDuration: number;

  @Expose()
  @Default(false)
  public isLte: boolean;

  @Expose()
  @Exclude({ toPlainOnly: true })
  @Type(() => SnOrganization)
  public organization: SnOrganization;

  @Expose()
  @Exclude({ toPlainOnly: true })
  public isShared: boolean;

  @Expose()
  @Exclude({ toPlainOnly: true })
  @Type(() => SnShareInfo)
  public shareInfo: SnShareInfo;

  @Expose()
  @Default(SnCameraManagementType.Managed)
  public managementType: SnCameraManagementType;

  @Expose()
  @Default(SnCameraUnmanagedType.JPEG)
  public unmanagedType: SnCameraUnmanagedType;

  @Expose()
  public unmanagedUrl: string;

  @Expose()
  public externalAppliance: string;

  @Expose()
  public externalCameraId: string;

  @Expose()
  public externalType: string;

  @Expose()
  public external: CameraExternal;

  @Expose()
  public ptzControlInvertX: boolean;

  @Expose()
  public ptzControlInvertY: boolean;

  @Expose()
  public ptzControlZoomOnly: boolean;

  @Expose()
  @ExposeRawData()
  public unmanagedParams: { [key: string]: any };

  @Expose()
  @Transform((value, data) => {
    if (
      data.managementType === SnCameraManagementType.Unmanaged &&
      data.unmanagedType &&
      data.unmanagedType !== SnCameraUnmanagedType.JPEG
    ) {
      return new SnStreamSources({
        [data.unmanagedType.toLowerCase()]: data.unmanagedUrl,
      });
    }
    return;
  })
  public unmanagedLiveUrls: SnStreamSources;

  @Expose()
  public aiDetectionTypes: SnCameraAiDetectionType[];

  @Expose()
  @Type(() => SnDVRStream)
  public prerecordingUrls: SnDVRStream;

  @Expose()
  @Exclude({ toPlainOnly: true })
  @Type(() => SnTag)
  @Default(new SnCollection())
  public tags: SnCollection<SnTag>;

  public readonly contentType = SnContentType.Camera;

  @Expose()
  @Type(() => SnDVRSources)
  public playbackUrls: SnDVRSources;

  @Expose()
  public vendorApis: any;

  @Expose()
  public statusChangeTimestamp: Date;

  @Expose()
  public descendantCameras: SnCamera[];

  @Expose()
  public parentCamera: string;

  @Expose()
  public isParentCamera: boolean;

  @Expose()
  public ptzControlEnableArrows: boolean;

  @Expose()
  @Default('relative')
  public ptzMovementType: string;

  @ExposeRawData()
  public descendantCount: number;

  @Expose()
  public isAxonCamera: boolean;

  @Expose()
  public axonDevice: FsAxonDevice;

  @Expose()
  public isPresenceCamera: boolean;

  @Expose()
  public presenceDevice: FsPresenceDevice;

  @Expose()
  public alternateId?: string;

  @Expose()
  public incidentId?: number | string;

  @Expose()
  public streamTokenExpiryTime?: string; // eg: '2025-07-30T22:14:50Z'

  public isFullscreen?: boolean;
  public isModemCamera?: boolean;
  public isSkydioCamera?: boolean;
  public busId?: string;
  public elevation: number;
  public infospotHeading?: number = 90;
  public layerObject: any;

  constructor(data: any = {}) {
    super();

    this.fromJSON(data);
  }

  get coords(): { lng: number; lat: number } {
    if (!this.geometry) {
      return null;
    }
    return {
      lng: this.geometry.coordinates[0],
      lat: this.geometry.coordinates[1],
    };
  }

  get isManaged(): boolean {
    return this.managementType === SnCameraManagementType.Managed;
  }

  get isExternal(): boolean {
    return this.managementType === SnCameraManagementType.External;
  }

  get isUav(): boolean {
    return this.cameraType?.name?.toLowerCase() === 'uav';
  }

  get liveIsAvailable(): boolean {
    return this.isLive && (!!this.unmanagedUrl || !!this.liveVideoUrls);
  }

  get subName(): string {
    const name = [];

    if (this.make) {
      name.push(this.make);
    }

    if (this.model) {
      name.push(this.model);
    }

    return name.join(' / ');
  }

  get canEdit(): boolean {
    return !this.isShared || this.shareInfo.permissions === SnSharePermissions.Edit;
  }

  get isOwned(): boolean {
    return !this.isShared || this.shareInfo.direction === 'Out';
  }

  private get _color(): string {
    switch (this.status) {
      case SnCameraStatus.Up:
        return COLORS['light-green-500'];
      case SnCameraStatus.Down:
        return COLORS['deep-orange-800'];
      case SnCameraStatus.Pending:
        return COLORS['amber-500'];
      case SnCameraStatus.Unconfigured:
        return COLORS['grey-700'];
      case SnCameraStatus.Unmanaged:
        return COLORS['primary-800'];
      default:
        return COLORS['grey-0'];
    }
  }

  public toGeoJSON(): Feature<Point> {
    return {
      type: 'Feature',
      geometry: this.geometry,
      id: this.id,
      properties: {
        id: this.id,
        name: this.name,
        heading: this.heading,
        altitude: this.altitude,
        disposition: this.disposition,
        fieldOfView: this.fieldOfView,
        color: this._color,
        floor: this.floor ? this.floor.name : '',
        location: this.location,
      },
    };
  }
}
